import type { FieldDef } from './fields'

/**
 * Normalizace hodnot do tvaru, kterému rozumí backend.
 *
 * Vrstva, která ve starém parseru chyběla úplně — proto na backend chodila
 * česká desetinná čárka i datum v libovolném formátu.
 */

/** Formát, který backend přijímá bez dalšího hádání. */
const OUT_DATE = 'YYYY-MM-DD HH:MM'

export type TransformResult = { value: string; error: string | null }

export function transformValue(field: FieldDef, raw: string): TransformResult {
    const value = (raw ?? '').trim()
    if (value === '') return { value: '', error: null }

    switch (field.type) {
        case 'number': {
            const n = normaliseNumber(value)
            return n === null ? { value, error: `není číslo („${value}“)` } : { value: n, error: null }
        }
        case 'date': {
            const d = normaliseDate(value)
            return d === null
                ? { value, error: `neplatný formát data („${value}“), očekáváno ${OUT_DATE}` }
                : { value: d, error: null }
        }
        case 'email': {
            if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)) {
                return { value, error: `neplatný e-mail („${value}“)` }
            }
            break
        }
        case 'currency': {
            if (!/^[A-Za-z]{3}$/.test(value)) {
                return { value, error: `měna musí mít 3 znaky („${value}“)` }
            }
            return { value: value.toUpperCase(), error: null }
        }
        default:
            break
    }

    // Ořez by znamenal tichou ztrátu dat — radši chyba, kterou uživatel uvidí.
    if (field.maxLength !== null && value.length > field.maxLength) {
        return { value, error: `překračuje ${field.maxLength} znaků (${value.length})` }
    }

    return { value, error: null }
}

/** `1 234,50` → `1234.50`. Česká lokalizace používá čárku a mezery po tisících. */
export function normaliseNumber(value: string): string | null {
    const cleaned = value.replace(/[\s\u00a0]/g, '').replace(',', '.')
    if (!/^[+-]?\d*\.?\d+$/.test(cleaned)) return null
    return cleaned
}

/**
 * Sjednotí datum do `YYYY-MM-DD HH:MM`.
 *
 * Bez času se doplní `00:00`; sekundy se zahazují (backend je nepotřebuje).
 */
export function normaliseDate(value: string): string | null {
    const v = value.trim()

    // 2026-08-03[ T]22:26[:00]
    let m = v.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{2})(?::\d{2})?)?/)
    if (m) return build(+m[1], +m[2], +m[3], +(m[4] ?? 0), +(m[5] ?? 0))

    // 3.8.2026 22:26  /  03. 08. 2026
    m = v.match(/^(\d{1,2})\.\s?(\d{1,2})\.\s?(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::\d{2})?)?/)
    if (m) return build(+m[3], +m[2], +m[1], +(m[4] ?? 0), +(m[5] ?? 0))

    return null
}

function build(y: number, mo: number, d: number, h: number, mi: number): string | null {
    if (mo < 1 || mo > 12 || d < 1 || d > 31 || h > 23 || mi > 59) return null

    // Odchytí i neexistující dny (31.02.) — Date by je přetočilo na březen.
    const probe = new Date(y, mo - 1, d, h, mi)
    if (probe.getFullYear() !== y || probe.getMonth() !== mo - 1 || probe.getDate() !== d) return null

    const p = (n: number) => String(n).padStart(2, '0')
    return `${y}-${p(mo)}-${p(d)} ${p(h)}:${p(mi)}`
}
